/**
 * The commissioned portrait — the agent's identity, framed.
 *
 * Every agent hangs in the same frame: portrait → fine keyline → a mat
 * of raised surface → the agent's accent ring. Until a portrait is
 * commissioned (served from /avatars/<id>.png), the frame holds a
 * typeset Fraunces monogram on an accent wash — stationery, never an
 * emoji floating in a circle.
 *
 * The frame is the constant; the face changes. Breathing class while
 * thinking applies to the whole frame; the ember (CSS) wraps outside.
 */
import { useState } from "react";

export function Avatar(props: {
  agentId: string;
  name: string;
  emoji?: string;
  size?: number;
  thinking?: boolean;
  accent?: string;
}) {
  const { agentId, name, size = 40, thinking = false } = props;
  const [imgFailed, setImgFailed] = useState(false);

  const accent = props.accent ?? "var(--agent-accent)";
  // Frame proportions: ring and mat scale gently with the portrait,
  // and the rendered box is always exactly `size` — frames never
  // shove the layout around.
  const ring = size >= 96 ? 2.5 : size >= 48 ? 2 : 1.5;
  const mat = size >= 96 ? 4 : 2;
  const inner = size - 2 * (ring + mat);

  return (
    <div
      className={`relative flex shrink-0 items-center justify-center rounded-full ${thinking ? "avatar-thinking" : ""}`}
      style={{
        width: size,
        height: size,
        background: "var(--surface)",
        boxShadow: `inset 0 0 0 ${ring}px ${accent}`,
      }}
    >
      {!imgFailed ? (
        <img
          src={`/avatars/${agentId}.png`}
          alt=""
          width={inner}
          height={inner}
          className="rounded-full object-cover"
          style={{
            width: inner,
            height: inner,
            boxShadow: "0 0 0 1px color-mix(in srgb, var(--ink) 14%, transparent)",
          }}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div
          aria-hidden
          className="flex items-center justify-center rounded-full font-display font-medium"
          style={{
            width: inner,
            height: inner,
            fontSize: inner * 0.46,
            color: accent,
            background: `color-mix(in srgb, ${accent} 11%, var(--surface))`,
            boxShadow: "0 0 0 1px color-mix(in srgb, var(--ink) 10%, transparent)",
          }}
        >
          {name.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
}
